import React, { useState, useEffect } from 'react';
import { postDecision } from '../../api/forumAPI';
import CustomSelect from '../../../../components/ui/CustomSelect';
import { useNotification } from '../../../../context/NotificationContext';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Gavel, LoaderCircle } from 'lucide-react';

export default function DecisionModal({ isOpen, onClose, dossierId, onSuccess, initialType, isDecideur }) {
 const { showToast } = useNotification();
 const [formData, setFormData] = useState({
 decision: '',
 comment: '',
 conditions: '',
 missingDocs: '',
 approvedAmount: '',
 structuringType: ''
 });

 useEffect(() => {
   if (isOpen) {
     setFormData({
       decision: initialType || (isDecideur ? '' : 'OBSERVATION'),
       comment: '',
       conditions: '',
       missingDocs: '',
       approvedAmount: '',
       structuringType: ''
     });
   }
 }, [isOpen, initialType, isDecideur]);
 
 const [loading, setLoading] = useState(false);

 const handleChange = (e) => {
 const { name, value } = e.target;
 setFormData(prev => ({ ...prev, [name]: value }));
 };

 const handleSubmit = async (e) => {
 e.preventDefault();
 setLoading(true);
 try {
 await postDecision(dossierId, formData);
 showToast("Décision enregistrée avec succès", "success");
 onSuccess();
 } catch (error) {
 console.error(error);
 showToast("Erreur lors de l'enregistrement de la décision", "error");
 } finally {
 setLoading(false);
 }
 };

 let decisionOptions = [];
 if (!isDecideur) {
   if (initialType === 'SUBMITTED') {
     decisionOptions = [{ value: 'SUBMITTED', label: 'Soumission au comité' }];
   } else {
     decisionOptions = [{ value: 'OBSERVATION', label: 'Observation' }];
   }
 } else {
   decisionOptions = [
     { value: '', label: 'Sélectionnez une décision...' },
     { value: 'APPROVED', label: 'Valider (Faisable)' },
     { value: 'APPROVED_WITH_CONDITIONS', label: 'Valider sous conditions' },
     { value: 'REQUEST_DOCS', label: "Demande d'éléments complémentaires" },
     { value: 'ON_HOLD', label: 'Mettre en attente' },
     { value: 'REJECTED', label: 'Refuser' },
     { value: 'OBSERVATION', label: 'Observation' }
   ];
   if (initialType === 'SUBMITTED') {
     decisionOptions.push({ value: 'SUBMITTED', label: 'Soumission au comité' });
   }
 }

 return (
 <AnimatePresence>
 {isOpen && (
 <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 sm:p-0">
 <motion.div
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 exit={{ opacity: 0 }}
 transition={{ duration: 0.2 }}
 className="fixed inset-0 bg-black/60 backdrop-blur-sm"
 onClick={onClose}
 />

 <motion.div
 initial={{ opacity: 0, scale: 0.95 }}
 animate={{ opacity: 1, scale: 1 }}
 exit={{ opacity: 0, scale: 0.95 }}
 transition={{ duration: 0.2, ease: "easeOut" }}
 className="relative bg-surface rounded-xl shadow-hp-xl border border-workspace w-full max-w-2xl max-h-[90vh] overflow-y-auto flex flex-col z-10"
 >
 <div className="flex justify-between items-center p-6 border-b border-workspace bg-surface-muted sticky top-0 z-20">
 <h2 className="text-xl font-bold text-primary flex items-center">
 <Gavel className="w-5 h-5 mr-2 text-tertiary" />
 {isDecideur ? "Rendre une décision" : "Ajouter une observation"}
 </h2>
 <button onClick={onClose} className="text-tertiary hover:text-primary transition-colors">
 <X className="w-5 h-5" />
 </button>
 </div>
 
 <form onSubmit={handleSubmit} className="p-6 space-y-4">
 <div>
 <label className="block text-sm font-semibold text-primary mb-1">Action *</label>
 <CustomSelect 
 value={formData.decision}
 onChange={(val) => setFormData(prev => ({ ...prev, decision: val }))}
 options={decisionOptions}
 />
 </div>

 <div>
 <label className="block text-sm font-semibold text-primary mb-1">Commentaire général</label>
 <textarea name="comment" rows={3} value={formData.comment} onChange={handleChange} className="workspace-input"></textarea>
 </div>

 {(formData.decision === 'APPROVED_WITH_CONDITIONS' || formData.decision === 'APPROVED') && (
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-workspace pt-4 mt-4">
 <div className="col-span-1 sm:col-span-2">
 <label className="block text-sm font-semibold text-primary mb-1">Conditions suspensives</label>
 <textarea name="conditions" rows={2} value={formData.conditions} onChange={handleChange} className="workspace-input"></textarea>
 </div>
 <div>
 <label className="block text-sm font-semibold text-primary mb-1">Montant accordé / validé (€)</label>
 <input type="number" name="approvedAmount" value={formData.approvedAmount} onChange={handleChange} placeholder="Ex: 500000" className="workspace-input" />
 </div>
 <div>
 <label className="block text-sm font-semibold text-primary mb-1">Type de montage préconisé</label>
 <input type="text" name="structuringType" value={formData.structuringType} onChange={handleChange} placeholder="ex: SAS dédiée" className="workspace-input" />
 </div>
 </div>
 )}

 {formData.decision === 'REQUEST_DOCS' && (
 <div className="border-t border-workspace pt-4 mt-4">
 <label className="block text-sm font-semibold text-primary mb-1">Éléments manquants *</label>
 <textarea name="missingDocs" rows={2} value={formData.missingDocs} onChange={handleChange} className="workspace-input" required></textarea>
 </div>
 )}

 <div className="flex justify-end space-x-3 pt-6 border-t border-workspace mt-6">
 <button type="button" onClick={onClose} disabled={loading} className="btn btn-secondary">Annuler</button>
 <button type="submit" disabled={loading || !formData.decision} className="btn btn-primary flex items-center justify-center gap-2">
 {loading ? (
   <>
     <LoaderCircle className="w-4 h-4 animate-spin" />
     Enregistrement...
   </>
 ) : "Enregistrer la décision"}
 </button>
 </div>
 </form>
 </motion.div>
 </div>
 )}
 </AnimatePresence>
 );
}
